import {
  Button,
  Callout,
  Dialog,
  DialogActions,
  DialogContent,
  DialogForm,
  DialogTitle,
} from 'bento';
import { Formik, FormikErrors, FormikProps } from 'formik';
import { PromiseProtoFunction } from '@tock/shared/utils/connect';
import { dashboard, lib } from 'types';
import { safeToNumber } from '@tock/coretypes';
import PasscodeInput, { PASSCODE_LENGTH } from 'components/passcode/PasscodeInput';

export enum PasscodeValidationError {
  BAD_REQUEST_PASSCODE_DUPLICATE = 2027,
  BAD_REQUEST_PASSCODE_INVALID_LENGTH = 2028,
  BAD_REQUEST_PASSCODE_CONSECUTIVE_DIGITS = 2029,
  BAD_REQUEST_PASSCODE_REPEATED_DIGITS = 2030,
}
interface Props {
  patronId: number;
  onSubmit: PromiseProtoFunction<
    (patronId: number, passcode: string) => ApiAction<dashboard.PatronPasscode>,
    dashboard.PatronPasscode
  >;
  onClose: () => void;
}

interface PasscodeFormValues {
  passcode: string;
  patronId: number;
}

const EditPasscodeDialog: React.FC<React.PropsWithChildren<Props>> = ({
  onSubmit,
  onClose,
  patronId,
}) => {
  const renderErrorMessage = (error: lib.ErrorMessageProto) => {
    switch (safeToNumber(error.errorCode)) {
      case PasscodeValidationError.BAD_REQUEST_PASSCODE_DUPLICATE:
        return (
          <span>
            <strong>Passcode already exists.</strong> This passcode is already in use, please try
            again with a different passcode.
          </span>
        );
      case PasscodeValidationError.BAD_REQUEST_PASSCODE_INVALID_LENGTH:
        return (
          <span>
            <strong>Unsupported passcode.</strong> Passcode must be exactly {PASSCODE_LENGTH}
            digits.
          </span>
        );
      case PasscodeValidationError.BAD_REQUEST_PASSCODE_CONSECUTIVE_DIGITS:
        return (
          <span>
            <strong>Unsupported passcode.</strong> Passcode cannot be entirely consecutive digits.
          </span>
        );
      case PasscodeValidationError.BAD_REQUEST_PASSCODE_REPEATED_DIGITS:
        return (
          <span>
            <strong>Unsupported passcode.</strong> Passcode has too many repeated digits.
          </span>
        );
      default:
        return error.message;
    }
  };

  const initFormValues: PasscodeFormValues = {
    passcode: '',
    patronId: patronId,
  };

  return (
    <Formik
      initialValues={initFormValues}
      validateOnMount
      onSubmit={(values, actions) => {
        actions.setStatus(undefined);
        onSubmit(values.patronId, values.passcode)
          .then(() => {
            onClose();
          })
          .catch((reason: lib.SingleResponseProto) => {
            actions.setStatus({ submitError: reason.error });
          });
      }}
      validate={validatePasscodeForm}
    >
      {({ isValid, values, setFieldValue, status }: FormikProps<PasscodeFormValues>) => (
        <Dialog open={true} fullWidth aria-labelledby="EditPasscodeDialog">
          <DialogForm>
            <DialogTitle id="EditPasscodeDialog" onClose={onClose}>
              Edit passcode
            </DialogTitle>
            <DialogContent>
              <div className="EditPasscodeModal">
                <div className="EditPasscodeModal-message">
                  {status ? (
                    <Callout variant="danger" className="EditPasscodeModal-message--shrink">
                      {renderErrorMessage(status.submitError)}
                    </Callout>
                  ) : (
                    `Enter ${PASSCODE_LENGTH} digit passcode to access specific actions.`
                  )}
                </div>
                <PasscodeInput
                  passcode={values.passcode}
                  showDigits={true}
                  onPasscodeChanged={(passcode: string) => setFieldValue('passcode', passcode)}
                  error={!!status}
                />
              </div>
            </DialogContent>
            <DialogActions>
              <Button color="primary" size="large" type="submit" disabled={!isValid}>
                Set passcode
              </Button>
            </DialogActions>
          </DialogForm>
        </Dialog>
      )}
    </Formik>
  );
};

const validatePasscodeForm = (values: PasscodeFormValues): FormikErrors<PasscodeFormValues> => {
  const errors: FormikErrors<PasscodeFormValues> = {};

  if (values.passcode.length < PASSCODE_LENGTH) {
    errors.passcode = 'Passcode is too short';
  }

  return errors;
};

export default EditPasscodeDialog;
